// apps/vendor/src/pages/auth/VerifyOtpPage.tsx
//
// Phase 1 Wave 9 Task 46 — OTP verification page (post-register, vendor mirror).
// Identical behaviour to merchant variant — purpose='register' for both roles.
// The vendor-vs-merchant divergence happens after survey submit (Task 32 sets
// vendor status → PendingApproval).
//
// Implements:
//   - CONTEXT.md — multi-channel OTP (SMS↔Email switch)
//   - CONTEXT.md — same-channel resend overwrites OTP (TTL reset
//                            handler keeps the SAME 6-digit code per AC-19)
//   - CONTEXT.md — Idempotency-Key (anon-scoped) per submit
//
// Flow:
//   1. Read `registerEmail` from sessionStorage (set by RegisterPage).
//      Falls back to a manual email input if missing (privacy mode / refresh).
//   2. User types 6-digit OTP → POST /auth/verify-otp { email, otpCode, purpose:'register' }
//   3. On success: server returns next='survey' → navigate /register/survey
//   4. Resend button → POST /auth/resend-otp { email|phone, purpose, channel? }
//   5. Channel switch button toggles SMS↔Email; clicking it triggers a NEW
//      OTP request via /auth/resend-otp with the explicit `channel` override.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { apiClient } from '@printway/shared/api';
import {
  AuthSplitLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
  useToast,
} from '@printway/ui';

import { useTranslation, t as i18nT } from '../../lib/i18n/index.js';

type OtpChannel = 'email' | 'sms' | 'lark' | 'voice';

const RESEND_COOLDOWN_DEFAULT_S = 60;

// dbg 20260703 — SMS OTP channel is disabled until an SMS gateway is wired.
// OTP is email-only (BE OTP_FORCE_EMAIL). Hide the "switch to SMS" affordance
// so users don't hit "Resend failed (Failed to fetch)" on a dead SMS path
// (#2375, #2377). Flip to true once SMS is provisioned + OTP_FORCE_EMAIL=false.
const SMS_OTP_ENABLED = false;

const VerifyOtpSchema = z.object({
  otpCode: z.string().regex(/^\d{6}$/, i18nT('auth:verifyOtp.validation.otpRegex')),
  email: z.string().email(i18nT('auth:verifyOtp.validation.emailInvalid')),
});
type VerifyOtpInput = z.infer<typeof VerifyOtpSchema>;

export default function VerifyOtpPage(): JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('auth');

  const initialEmail = useMemo<string>(() => readEmailFromSession(), []);
  const initialChannel = useMemo<OtpChannel>(
    () => readChannelFromSession() ?? 'email',
    [],
  );

  const [needsEmailEntry, setNeedsEmailEntry] = useState<boolean>(
    () => initialEmail === '',
  );
  const [channel, setChannel] = useState<OtpChannel>(initialChannel);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [isSwitchingChannel, setIsSwitchingChannel] = useState<boolean>(false);
  const [cooldownS, setCooldownS] = useState<number>(() => computeInitialCooldown());

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<VerifyOtpInput>({
    resolver: zodResolver(VerifyOtpSchema),
    defaultValues: { otpCode: '', email: initialEmail },
  });

  const otpCode = watch('otpCode');
  const email = watch('email');

  // Tick down resend cooldown each second.
  useEffect(() => {
    if (cooldownS <= 0) return;
    const handle = setInterval(() => {
      setCooldownS((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(handle);
  }, [cooldownS]);

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const onSubmit = async (values: VerifyOtpInput): Promise<void> => {
    setSubmitError(null);
    try {
      const idempotencyKey = generateIdempotencyKey();
      const client = apiClient();
      const { data, error, response } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown; headers?: Record<string, string> },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
        }
      ).POST('/api/v1/auth/verify-otp', {
        body: {
          email: values.email.trim().toLowerCase(),
          otpCode: values.otpCode,
          purpose: 'register',
        },
        headers: {
          'Idempotency-Key': idempotencyKey,
          'Content-Type': 'application/json',
        },
      });

      if (error || !response.ok) {
        const code = extractErrorCode(error) ?? `HTTP_${response.status}`;
        const msg = humanizeOtpError(code);
        setSubmitError(msg);
        if (code === 'AUTH_OTP_INVALID' || code === 'AUTH_OTP_EXPIRED') {
          setError('otpCode', { type: 'manual', message: msg });
        }
        toast({ variant: 'error', title: t('verifyOtp.toast.errorTitle'), description: msg });
        return;
      }

      const payload = unwrap<{
        ok?: boolean;
        next?: 'survey' | 'login' | 'reset' | 'pending_approval';
      }>(data);
      const nextStep = payload.next ?? 'survey';
      if (nextStep === 'pending_approval') {
        // 2026-09-09 vendor-register redesign — vendors skip the survey and
        // land directly in the admin approval queue. Tell them what happens
        // next, then send them to /login (which blocks with
        // AUTH_VENDOR_PENDING_APPROVAL until an admin approves).
        toast({
          variant: 'success',
          title: t('verifyOtp.toast.pendingApprovalTitle'),
          description: t('verifyOtp.toast.pendingApprovalDescription'),
        });
        navigate('/login');
        return;
      }
      toast({
        variant: 'success',
        title: t('verifyOtp.toast.successTitle'),
        description: t('verifyOtp.toast.successDescription'),
      });
      if (nextStep === 'survey') {
        navigate('/register/survey');
      } else if (nextStep === 'login') {
        navigate('/login');
      } else {
        navigate('/reset-password');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('verifyOtp.error.fallback');
      setSubmitError(msg);
      toast({ variant: 'error', title: t('verifyOtp.toast.errorTitle'), description: msg });
    }
  };

  // -------------------------------------------------------------------------
  // Resend (same channel) — overwrites OTP (AC-19).
  // -------------------------------------------------------------------------

  const onResend = useCallback(async (): Promise<void> => {
    if (cooldownS > 0 || isResending) return;
    if (!email || !/.+@.+\..+/.test(email)) {
      setSubmitError(t('verifyOtp.error.emailInvalidResend'));
      return;
    }
    setIsResending(true);
    setSubmitError(null);
    try {
      const idempotencyKey = generateIdempotencyKey();
      const result = await callResendOtp({
        email: email.trim().toLowerCase(),
        purpose: 'register',
        channel,
        idempotencyKey,
      });
      if (result.ok) {
        const next = Math.max(result.nextResendAfter ?? RESEND_COOLDOWN_DEFAULT_S, 5);
        setCooldownS(next);
        toast({
          variant: 'success',
          title: t('verifyOtp.toast.resendSuccessTitle'),
          description: result.masked
            ? t('verifyOtp.toast.resendSuccessTo', { masked: result.masked })
            : t('verifyOtp.toast.resendSuccessGeneric'),
        });
      } else {
        setSubmitError(humanizeResendError(result.code));
        toast({
          variant: 'error',
          title: t('verifyOtp.toast.resendErrorTitle'),
          description: humanizeResendError(result.code),
        });
      }
    } finally {
      setIsResending(false);
    }
  }, [cooldownS, isResending, email, channel, toast, t]);

  // -------------------------------------------------------------------------
  // Channel switch (SMS↔Email) — issues a NEW OTP.
  // -------------------------------------------------------------------------

  const onSwitchChannel = useCallback(async (): Promise<void> => {
    if (isSwitchingChannel) return;
    if (!email || !/.+@.+\..+/.test(email)) {
      setSubmitError(t('verifyOtp.error.emailInvalidResend'));
      return;
    }
    const targetChannel: OtpChannel = channel === 'email' ? 'sms' : 'email';
    setIsSwitchingChannel(true);
    setSubmitError(null);
    try {
      const idempotencyKey = generateIdempotencyKey();
      const result = await callResendOtp({
        email: email.trim().toLowerCase(),
        purpose: 'register',
        channel: targetChannel,
        idempotencyKey,
      });
      if (result.ok) {
        setChannel(targetChannel);
        try {
          sessionStorage.setItem('registerOtpChannel', targetChannel);
        } catch {
          /* noop */
        }
        const next = Math.max(result.nextResendAfter ?? RESEND_COOLDOWN_DEFAULT_S, 5);
        setCooldownS(next);
        toast({
          variant: 'success',
          title: t('verifyOtp.toast.switchSuccessTitle', { channel: channelLabel(targetChannel) }),
          ...(result.masked ? { description: t('verifyOtp.toast.switchSuccessTo', { masked: result.masked }) } : {}),
        });
      } else {
        setSubmitError(humanizeResendError(result.code));
        toast({
          variant: 'error',
          title: t('verifyOtp.toast.switchErrorTitle'),
          description: humanizeResendError(result.code),
        });
      }
    } finally {
      setIsSwitchingChannel(false);
    }
  }, [isSwitchingChannel, channel, email, toast, t]);

  // Sanitize OTP input — keep only digits, max 6.
  const onOtpChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue('otpCode', cleaned, { shouldValidate: true });
  };

  const altChannelLabel = channelLabel(channel === 'email' ? 'sms' : 'email');
  const cooldownLabel = cooldownS > 0 ? ` (${cooldownS}s)` : '';

  return (
    <AuthSplitLayout variant="verification">
      <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('verifyOtp.title')}</CardTitle>
          <CardDescription>
            {t('verifyOtp.descriptionPrefix')} {channelLabel(channel)}.
            {email && !needsEmailEntry ? (
              <>
                {' '}
                <span className="font-medium text-pw-text">{maskEmail(email)}</span>
              </>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit(onSubmit)} noValidate>
            {needsEmailEntry ? (
              <FormField>
                <FormLabel htmlFor="otp-email">{t('verifyOtp.labelEmail')}</FormLabel>
                <Input
                  id="otp-email"
                  type="email"
                  autoComplete="email"
                  disabled={isSubmitting}
                  {...register('email', {
                    onChange: () => setNeedsEmailEntry(true),
                  })}
                />
                {errors.email ? (
                  <FormMessage>{errors.email.message}</FormMessage>
                ) : null}
              </FormField>
            ) : (
              <input type="hidden" {...register('email')} />
            )}

            <FormField>
              <FormLabel htmlFor="otp-code">{t('verifyOtp.labelOtp')}</FormLabel>
              <Input
                id="otp-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                aria-invalid={Boolean(errors.otpCode)}
                disabled={isSubmitting}
                value={otpCode}
                onChange={onOtpChange}
                className="text-center text-lg tracking-[0.5em]"
              />
              {errors.otpCode ? (
                <FormMessage>{errors.otpCode.message}</FormMessage>
              ) : null}
            </FormField>

            {submitError ? (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
              >
                {submitError}
              </div>
            ) : null}

            <Button
              type="submit"
              variant="default"
              size="lg"
              className="w-full"
              disabled={isSubmitting || (otpCode?.length ?? 0) !== 6}
            >
              {isSubmitting ? <Spinner size="sm" /> : t('verifyOtp.submit')}
            </Button>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Button
                type="button"
                variant="outline"
                size="default"
                onClick={onResend}
                disabled={cooldownS > 0 || isResending || isSubmitting}
              >
                {isResending ? <Spinner size="sm" /> : `${t('verifyOtp.resendButton')}${cooldownLabel}`}
              </Button>
              {SMS_OTP_ENABLED ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="default"
                  onClick={onSwitchChannel}
                  disabled={isSwitchingChannel || isSubmitting}
                >
                  {isSwitchingChannel ? (
                    <Spinner size="sm" />
                  ) : (
                    t('verifyOtp.switchTo', { channel: altChannelLabel })
                  )}
                </Button>
              ) : null}
            </div>
          </Form>
        </CardContent>
        <CardFooter className="justify-center">
          <button
            type="button"
            className="text-sm text-pw-text-muted underline"
            onClick={() => navigate('/register')}
          >
            {t('verifyOtp.backToRegister')}
          </button>
        </CardFooter>
      </Card>
    </div>
    </AuthSplitLayout>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readEmailFromSession(): string {
  try {
    return sessionStorage.getItem('registerEmail') ?? '';
  } catch {
    return '';
  }
}

function readChannelFromSession(): OtpChannel | null {
  try {
    const v = sessionStorage.getItem('registerOtpChannel');
    if (v === 'email' || v === 'sms' || v === 'lark' || v === 'voice') return v;
    return null;
  } catch {
    return null;
  }
}

function computeInitialCooldown(): number {
  try {
    const iso = sessionStorage.getItem('registerResendAvailableAt');
    if (!iso) return 0;
    const at = Date.parse(iso);
    if (Number.isNaN(at)) return 0;
    const diff = Math.ceil((at - Date.now()) / 1000);
    return diff > 0 ? Math.min(diff, 600) : 0;
  } catch {
    return 0;
  }
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

interface ResendArgs {
  email: string;
  purpose: 'register';
  channel: OtpChannel;
  idempotencyKey: string;
}
interface ResendResult {
  ok: boolean;
  nextResendAfter?: number;
  channel?: OtpChannel;
  masked?: string;
  code?: string;
}

async function callResendOtp(args: ResendArgs): Promise<ResendResult> {
  try {
    const client = apiClient();
    const { data, error, response } = await (
      client as unknown as {
        POST: (
          path: string,
          init: { body: unknown; headers?: Record<string, string> },
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
      }
    ).POST('/api/v1/auth/resend-otp', {
      body: {
        email: args.email,
        purpose: args.purpose,
        channel: args.channel,
      },
      headers: {
        'Idempotency-Key': args.idempotencyKey,
        'Content-Type': 'application/json',
      },
    });
    if (error || !response.ok) {
      const code = extractErrorCode(error) ?? `HTTP_${response.status}`;
      return { ok: false, code };
    }
    const payload = unwrap<{
      nextResendAfter?: number;
      channel?: OtpChannel;
      masked?: string;
    }>(data);
    return {
      ok: true,
      ...(payload.nextResendAfter !== undefined
        ? { nextResendAfter: payload.nextResendAfter }
        : {}),
      ...(payload.channel ? { channel: payload.channel } : {}),
      ...(payload.masked ? { masked: payload.masked } : {}),
    };
  } catch (err) {
    return { ok: false, code: err instanceof Error ? err.message : 'NETWORK_ERROR' };
  }
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as { success?: boolean }).success === true &&
    'data' in payload
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function extractErrorCode(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    (payload as { error?: { code?: string } }).error?.code
  ) {
    return (payload as { error: { code: string } }).error.code;
  }
  return null;
}

function channelLabel(c: OtpChannel): string {
  switch (c) {
    case 'email':
      return i18nT('auth:verifyOtp.channel.email');
    case 'sms':
      return i18nT('auth:verifyOtp.channel.sms');
    case 'lark':
      return i18nT('auth:verifyOtp.channel.lark');
    case 'voice':
      return i18nT('auth:verifyOtp.channel.voice');
    default:
      return c;
  }
}

function maskEmail(addr: string): string {
  const at = addr.indexOf('@');
  if (at <= 1) return addr;
  const local = addr.slice(0, at);
  const domain = addr.slice(at);
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${'*'.repeat(Math.max(local.length - visible.length, 1))}${domain}`;
}

function humanizeOtpError(code: string): string {
  switch (code) {
    case 'AUTH_OTP_INVALID':
      return i18nT('auth:verifyOtp.otpError.invalid');
    case 'AUTH_OTP_EXPIRED':
      return i18nT('auth:verifyOtp.otpError.expired');
    case 'RATE_LIMIT':
      return i18nT('auth:verifyOtp.otpError.rateLimit');
    case 'VALIDATION_FAILED':
      return i18nT('auth:verifyOtp.otpError.validation');
    default:
      return i18nT('auth:verifyOtp.otpError.fallback', { code });
  }
}

function humanizeResendError(code?: string): string {
  switch (code) {
    case 'AUTH_OTP_RESEND_TOO_FAST':
      return i18nT('auth:verifyOtp.resendError.tooFast');
    case 'AUTH_OTP_CHANNEL_QUOTA_EXCEEDED':
      return i18nT('auth:verifyOtp.resendError.quota');
    case 'AUTH_OTP_NOT_FOUND':
      return i18nT('auth:verifyOtp.resendError.notFound');
    case 'RATE_LIMIT':
      return i18nT('auth:verifyOtp.resendError.rateLimit');
    default:
      return code ? i18nT('auth:verifyOtp.resendError.fallback', { code }) : i18nT('auth:verifyOtp.resendError.fallbackNoCode');
  }
}
